import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { RootState } from '@/core/store'

/**
 * Custom hook that automatically merges the correct account-scoping params
 * into the config based on the current context:
 *
 * - selected account + fetch-all mode enabled:
 *     `using_account_id` + `fetch_all_data=1`
 *     → scopes to that account and all its descendants
 *
 * - selected account + fetch-all mode disabled:
 *     `using_account_id`
 *     → scopes to that account only (existing behavior)
 *
 * - no selected account + fetch-all mode enabled:
 *     `fetch_all_data=1`
 *     → returns full account hierarchy data (no `without_children_data`)
 *
 * - no selected account + fetch-all mode disabled + `withoutChildrenDataWhenUnselected`:
 *     `without_children_data=true`
 *     → top-level data only (existing behavior for the four main list endpoints)
 *
 * - no selected account + fetch-all mode disabled + no flag:
 *     no extra params (existing default)
 *
 * @param baseConfig - The base configuration object
 * @param withoutChildrenDataWhenUnselected - When true, inject
 *   `without_children_data: true` if no account is selected and fetch-all mode
 *   is off. Use this for the four `my/*` list endpoints (zones, locations,
 *   groups, announcements).
 * @returns Config with the appropriate account-context params merged
 */
export const useAccountConfig = <T extends { params?: any } | undefined>(
	baseConfig?: T,
	withoutChildrenDataWhenUnselected?: boolean
): T => {
	const selectedAccount = useSelector(
		(state: RootState) => state.selectedAccount.selectedAccount
	)

	const fetchAllDataEnabled = useSelector(
		(state: RootState) => state.fetchAllData.enabled
	)

	return useMemo(() => {
		if (selectedAccount?.id) {
			if (fetchAllDataEnabled) {
				return {
					...baseConfig,
					params: {
						...baseConfig?.params,
						using_account_id: selectedAccount.id,
						fetch_all_data: 1,
					},
				} as T
			}

			return {
				...baseConfig,
				params: {
					...baseConfig?.params,
					using_account_id: selectedAccount.id,
				},
			} as T
		}

		// No selected account
		if (fetchAllDataEnabled) {
			return {
				...baseConfig,
				params: {
					...baseConfig?.params,
					fetch_all_data: 1,
				},
			} as T
		}

		if (withoutChildrenDataWhenUnselected) {
			return {
				...baseConfig,
				params: {
					...baseConfig?.params,
					without_children_data: true,
				},
			} as T
		}

		return baseConfig as T
	}, [
		baseConfig,
		selectedAccount?.id,
		fetchAllDataEnabled,
		withoutChildrenDataWhenUnselected,
	])
}
